import { DEFAULT_PAGE_INFO } from '@apeiron/common'
import { useFrontendConfigContext } from '@apeiron/config'
import { PlanetLite } from '@apeiron/planet'
import { PageInfo } from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import { planetLiteSchema } from '@src/deserialize/yup/planet'
import graphql from '@src/graphql/query/getMySeedPlanets'
import { PlanetLiteDeserializeOption } from '@src/types/deserialize'
import { deserializePlanetLite } from '@src/util/apeiron/planet'
import * as R from 'ramda'
import { useCallback, useState } from 'react'

const useGetMySeedPlanets = (): Hook => {
  const [getSeedPlanets, { loading, called }] = useLazyQuery(graphql)

  const { config } = useFrontendConfigContext()

  const [data, setData] = useState<PlanetLite[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (option: PlanetLiteDeserializeOption = {}): Promise<Response> => {
      const response = await getSeedPlanets({ variables: {} })

      const planets = R.map((planetData: Record<string, any>) => {
        const planet = planetLiteSchema.cast(planetData, {
          context: { data: config }
        })

        // ! hard coded to detect seed planet here, should detect by api response later
        const seedPlanet = {
          ...planet,
          seed: true
        }

        return deserializePlanetLite(seedPlanet, option)
      }, R.pathOr([], ['data', 'getMySeedPlanet', 'planets'], response))

      const page = {
        ...pageInfo,
        total: R.length(planets),
        totalPage: 1
      }

      setData(planets)
      setPageInfo(page)

      return {
        data: planets,
        pageInfo: page
      }
    },
    [config, getSeedPlanets, pageInfo]
  )

  const clean = useCallback(() => {
    setData([])
  }, [])

  return {
    called,
    data,
    loading,
    pageInfo,
    clean,
    fetch
  }
}

type Response = {
  data: PlanetLite[]
  pageInfo: PageInfo
}

type Hook = {
  called: boolean
  data: PlanetLite[]
  loading: boolean
  pageInfo: PageInfo
  clean: () => void
  fetch: (option?: PlanetLiteDeserializeOption) => Promise<Response>
}

export default useGetMySeedPlanets
